import { z, isoZ } from 'my-zod'
import { dateFns } from 'iso-fns2'
import { attachmentModel } from '../../utilities/attachment'

export const billingCaseLifecycleStatusEnum = z
  .enum([
    'Draft',
    'Void',
    'PendingLeadApproval',
    'PendingManagerApproval',
    'PendingFinanceApproval',
    'ProcessingWithFinance',
    'Complete'
  ])
  .setEnumLabels({
    Draft: 'Draft',
    Void: 'Void',
    PendingLeadApproval: 'Pending Lead Approval',
    PendingManagerApproval: 'Pending Manager Approval',
    PendingFinanceApproval: 'Pending Finance Approval',
    ProcessingWithFinance: 'Processing',
    Complete: 'Closed'
  })

export type billingCaseLifecycleStatusEnum = z.infer<typeof billingCaseLifecycleStatusEnum>

export const billingCaseCurrencyEnum = z.enum(['USD', 'CAD'])

export const billingCaseAdjustmentTypeEnum = z
  .enum(['Credit', 'AdditionalCharge', 'NotNeeded', 'PartsCredit', 'DiscontinueFutureBillings'])
  .setEnumLabels({
    Credit: 'Credit',
    AdditionalCharge: 'Additional Charge',
    NotNeeded: 'Not Needed',
    PartsCredit: 'Parts Credit',
    DiscontinueFutureBillings: 'Discontinue Future Billings'
  })

export const billingCasePaymentMethodEnum = z
  .enum(['COR360', 'DEX', 'MailedCheck', 'Other', 'PartsAccountCredit', 'ProgramInvoice', 'StripeBook'])
  .setEnumLabels({
    ProgramInvoice: 'Program Invoice',
    COR360: 'COR360',
    PartsAccountCredit: 'Parts Account Credit',
    MailedCheck: 'Mailed Check',
    DEX: 'DEX',
    StripeBook: 'Stripe ("Book")',
    Other: 'Other'
  })

export type billingCasePaymentMethodEnum = z.infer<typeof billingCasePaymentMethodEnum>

export const billingCaseReasonCodeEnum = z
  .enum([
    'DemoRidePayout',
    'DiscountProgram',
    'DWPartsLabor',
    'DWDeductible',
    'DWInternalCheckoutDeductible',
    'DWSelectMemberDeductible',
    'FleetAdjustment',
    'InvoiceError',
    'LateVehicleDelivery',
    'MarketingCoOp',
    'MPWRPlatformFee',
    'ParkingTollViolation',
    'PartsAndEquipmentIPadDigitalWrench',
    'PartsAndEquipmentPGA',
    'Recall',
    'RideFeeAdjustment',
    'SeasonExtension',
    'SelectReimbursement',
    'TitleAndRegistration',
    'TruncatedSeason',
    'VehicleDown',
    'VehicleOrderDiscontinued'
  ])
  .setEnumLabels({
    DemoRidePayout: 'Demo Ride Payout',
    DiscountProgram: 'Discount Program',
    DWPartsLabor: 'DW Parts/Labor',
    DWDeductible: 'DW Deductible',
    DWInternalCheckoutDeductible: 'DW Internal Checkout Deductible',
    DWSelectMemberDeductible: 'DW Select Member Deductible',
    FleetAdjustment: 'Fleet Adjustment',
    InvoiceError: 'Invoice Error',
    LateVehicleDelivery: 'Late Vehicle Delivery',
    MarketingCoOp: 'Marketing Co-Op',
    MPWRPlatformFee: 'MPWR Platform Fee',
    ParkingTollViolation: 'Parking/Toll Violation',
    PartsAndEquipmentIPadDigitalWrench: 'Parts & Equipment - iPad/Digital Wrench',
    PartsAndEquipmentPGA: 'Parts & Equipment - PG&A',
    Recall: 'Recall',
    RideFeeAdjustment: 'Ride Fee Adjustment',
    SeasonExtension: 'Season Extension',
    SelectReimbursement: 'Select Reimbursement',
    TitleAndRegistration: 'Title & Registration',
    TruncatedSeason: 'Truncated Season',
    VehicleDown: 'Vehicle Down',
    VehicleOrderDiscontinued: 'Vehicle Order Discontinued'
  })

export type billingCaseReasonCodeEnum = z.infer<typeof billingCaseReasonCodeEnum>

export const billingCaseDiscountProgram = z
  .enum(['SnowLogistics', 'FiftyOnFiftyPlan', 'PausePlan', 'Terminate', 'CoiledSpringProgram', 'JuneRelief'])
  .setEnumLabels({
    SnowLogistics: 'Snow Logistics',
    FiftyOnFiftyPlan: '50/50 Plan',
    PausePlan: 'Pause Plan',
    Terminate: 'Terminate',
    CoiledSpringProgram: 'Coiled Spring Program',
    JuneRelief: 'June Relief'
  })

export const billingCaseModel = z
  .object({
    shortId: z.string(),
    outfitterId: z.number().min(1, 'Required').setLabel('Outfitter Name'),
    ownerUserId: z.number().min(1, 'Required').setLabel('Case Owner'),
    createdUserId: z.number().min(1, 'Required').setLabel('Created By'),
    subject: z.string().nonempty().setLabel('Subject'),
    notes: z.string(),
    damageWaiverCaseShortId: z.string().nullable(),
    damageWaiverPartOrderId: z.string().uuid().nullable(),
    lifecycleStatus: billingCaseLifecycleStatusEnum.setLabel('Status'),
    amount: z.number().min(0).setLabel('Credit Requested'),
    currency: billingCaseCurrencyEnum,
    adjustmentType: billingCaseAdjustmentTypeEnum.setLabel('Type of Adjustment'),
    paymentMethod: billingCasePaymentMethodEnum.nullable(),
    paymentMethodOther: z.string().max(512).nullable(),
    customerOrderId: z.string().nullable(),
    externalInvoiceNumber: z.string().nullable().setLabel('Invoice Number to Adjust'),
    explanation: z.string().nonempty('Please provide description of situation.').setLabel('Description of Situation'),
    recommendation: z
      .string()
      .nonempty('Please provide account manager recommendation / justification.')
      .setLabel('Account Manager Recommendation/Justification'),
    reasonCode: billingCaseReasonCodeEnum,
    discountProgram: billingCaseDiscountProgram.nullable(),
    salesQuoteHeaderId: z.number().nullable().setLabel('Vehicle Order Number'),
    creditStartDate: isoZ.date().nullable().setLabel('Credit Start Date'),
    creditEndDate: isoZ.date().nullable().setLabel('Credit End Date'),
    attachments: attachmentModel.array(),
    lastModifiedByUserId: z.number().setLabel('Last Modified By'),
    completionDate: isoZ.instant().nullable().setLabel('Closed Date'),
    createdDate: isoZ.instant(),
    deletedDate: isoZ.instant().nullable(),
    lastModifiedDate: isoZ.instant()
  })
  .setCFV({
    paymentMethodShouldBeSpecified(val) {
      if (!val.paymentMethodOther && val.paymentMethod === billingCasePaymentMethodEnum.Enum.Other) {
        return {
          message: 'Payment method should be specified',
          path: ['paymentMethodOther']
        }
      }
    },
    discountProgramRequired(val) {
      if (val.reasonCode === billingCaseReasonCodeEnum.Enum.DiscountProgram && !val.discountProgram) {
        return {
          message: 'Please provide a discount program.',
          path: ['discountProgram']
        }
      }
    },
    salesQuoteHeaderIdRequired(val) {
      if (
        [
          billingCaseReasonCodeEnum.Enum.SeasonExtension,
          billingCaseReasonCodeEnum.Enum.TruncatedSeason,
          billingCaseReasonCodeEnum.Enum.FleetAdjustment
          // @ts-ignore
        ].includes(val.reasonCode) &&
        !Number.isFinite(val.salesQuoteHeaderId)
      ) {
        return {
          message: 'Please provide a vehicle order number.',
          path: ['salesQuoteHeaderId']
        }
      }
    },
    creditDates(val) {
      if (val.creditStartDate && val.creditEndDate && dateFns.isAfter(val.creditStartDate, val.creditEndDate)) {
        return {
          message: 'Credit start date should be before credit end date.',
          path: ['creditStartDate']
        }
      }
    }
  })

export type billingCaseModel = z.infer<typeof billingCaseModel>
